// NotesApp source file: cards.component.ts

import {
  ComponentFactoryResolver,
  Input,
  Output,
  EventEmitter,
  Injector,
  ApplicationRef,
  AfterViewInit,
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ViewContainerRef,
  computed,
  inject,
  HostListener,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

import { ActivatedRoute, RouterModule, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { filter, take } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';

// PRIMENG GUI COMPONENTS
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { StyleClassModule } from 'primeng/styleclass';
import { MenuItem, MessageService } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { InputTextModule } from 'primeng/inputtext';
import { BlockUIModule } from 'primeng/blockui';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Table, TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';

// Notes-App Services
import {
  Note,
  Project,
  Status,
  NoteType,
  DataService,
  Category,
  Tag,
} from '../../../_service/data.service';
import { LayoutService } from '../../../_service/layout.service';
import { AlertService } from '../../../_service/alert.service';
import { LogService } from '../../../_service/log.service';
import { FilterParams, FilterService } from '../../../_service/filter.service';
import { UserSettingsService } from '../../../_service/user-settings.service';

type CardSort = {
  project_id: number;
  sort_order: number;
};

type TagDisplayItem = {
  name: string;
  color?: string;
};

const LIST_MOBILE_TABLE_STATE_KEY = 'notesapp.home.list.mobile.v1';
const LIST_DESKTOP_TABLE_STATE_KEY = 'notesapp.home.list.desktop.v1';
// Exported so the bulk "Export Notes..." action's "Default (as current view)"
// sort option can read the same persisted List column-sort without duplicating
// the storage key (see BulkActionsLogic.sortNoteIdsForExport).
export const LIST_SORT_STATE_KEY = 'notesapp.home.list.sort.v1';
export const LIST_SORT_STATE_DEFAULT: { sortBy: string; sortOrder: 'asc' | 'desc' } = {
  sortBy: 'Modified',
  sortOrder: 'desc',
};

// Columns that should default to descending order the first time they're clicked.
const DEFAULT_DESC_SORT_COLUMNS = new Set(['Modified', 'Size']);

// Every combination of Note to Catgory, plus flag if it is to be displayed
type NoteCategory = {
  NoteID: string;
  CategoryID: string;
  isActive: boolean;
};

@Component({
  selector: 'app-list',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    RouterModule,
    MenuModule,
    InputTextModule,
    StyleClassModule,
    SelectModule,
    ButtonModule,
    BlockUIModule,
    DialogModule,
    ProgressSpinnerModule,
    TableModule,
    CheckboxModule,
  ],
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListComponent implements OnInit, OnDestroy {
  readonly mobileTableStateKey = LIST_MOBILE_TABLE_STATE_KEY;
  readonly desktopTableStateKey = LIST_DESKTOP_TABLE_STATE_KEY;

  @ViewChild('mobileTable') mobileTable?: Table;
  @ViewChild('desktopTable') desktopTable?: Table;
  @Input() notes: Note[] = [];
  @Input() categories: Category[] = [];
  @Input() tags: Tag[] = [];
  @Input() filter: FilterParams = {};
  @Input() isSelectMode: boolean = false;
  @Input() selectedNoteIds: string[] = [];
  @Output() selectedNoteIdsChange = new EventEmitter<string[]>();


  data: Note[] = [];
  paginatedData: Note[] = [];
  currentPage: number = 1;
  pageSize: number = 10;
  sortBy: string = 'Modified';
  sortOrder: 'asc' | 'desc' = 'desc';
  isResizing: boolean = false;
  //projects: Project[] | undefined;
  card_sort: CardSort[] = [];
  statusValues: string[] | undefined;
  types: string[] | undefined;
  isHeaderVisible: boolean = true;
  isWorkToggle: boolean = false; // Initialize based on your requirement
  selectedProjectId: number | null = null; // selected project ID for export PDF function
  loadingCounter = 0; // initially count of loading objects in progress
  isOnline: boolean = true; //??

  layoutService: LayoutService = inject(LayoutService);
  isDarkMode = computed(() => this.layoutService.appState().darkMode);

  allNotesByCategory: NoteCategory[] = [];
  private readonly patchedTables = new WeakSet<Table>();

  synchLog$: Observable<string[]>;

  /** IDs of notes the user has marked as favorite (user-settings), for the ♥ marker next to the title. */
  private favoriteNoteIds = new Set<string>();
  private favoriteNoteIdsSub?: Subscription;

  constructor(
    private filterService: FilterService,
    private dataService: DataService,
    private route: ActivatedRoute,
    private router: Router,
    private injector: Injector,
    private cdr: ChangeDetectorRef,
    private alertService: AlertService,
    private logs: LogService,
    private messages: MessageService,
    private userSettings: UserSettingsService,
  ) {
    this.synchLog$ = this.logs.entries$('synch');
    // this.allNotesByCategory = this.mapNotesToCategories(this.notes);
    // console.log('CardsComponent - allNotesByCategory:', this.allNotesByCategory);
  }

  ngOnInit(): void {
    console.log('ListComponent - in ngOnInit()');
    this.loadPersistedSort();
    // ngOnChanges (which seeds sortedNotes) runs before ngOnInit, using the
    // sortBy/sortOrder defaults — recompute now that the persisted sort is loaded.
    this.recomputeSortedNotes();

    this.favoriteNoteIdsSub = this.userSettings.userSettings$.subscribe((settings) => {
      const ids = Array.isArray(settings?.favoriteNoteIds) ? settings!.favoriteNoteIds! : [];
      this.favoriteNoteIds = new Set(ids.filter((id): id is string => typeof id === 'string'));
      // External async source under OnPush — mark for check so the ♥ markers refresh.
      this.cdr.markForCheck();
    });
  }

  isFavoriteNote(note: Note): boolean {
    return !!note._id && this.favoriteNoteIds.has(note._id);
  }

  private loadPersistedSort(): void {
    try {
      const raw = localStorage.getItem(LIST_SORT_STATE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (typeof parsed?.sortBy === 'string' && (parsed?.sortOrder === 'asc' || parsed?.sortOrder === 'desc')) {
        this.sortBy = parsed.sortBy;
        this.sortOrder = parsed.sortOrder;
      }
    } catch {
      // Ignore malformed/unavailable storage; fall back to defaults.
    }
  }

  private persistSort(): void {
    try {
      localStorage.setItem(LIST_SORT_STATE_KEY, JSON.stringify({ sortBy: this.sortBy, sortOrder: this.sortOrder }));
    } catch {
      // Ignore storage failures (e.g. private browsing quota).
    }
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      this.patchColumnResizeForZoom(this.mobileTable);
      this.patchColumnResizeForZoom(this.desktopTable);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['notes'] && changes['notes'].currentValue) {
      this.allNotesByCategory = this.mapNotesToCategories(this.notes);
      this.categories.sort((a, b) => a.Path.localeCompare(b.Path));
      // console.log('CategoryCardsComponent - updated allNotesByCategory:', this.allNotesByCategory);
    }

    // notes/tags drive both the tag-chip lookup and (via the Tags column) the
    // sort order, so keep both caches in lockstep whenever either input changes —
    // recomputing per-render (the old behavior) meant re-sorting the full list and
    // rebuilding a tag-id Map for every row on every single change-detection tick.
    if (changes['notes'] || changes['tags']) {
      this.rebuildTagDisplayCache();
      this.recomputeSortedNotes();
    }
  }

  ngOnDestroy() {
    this.favoriteNoteIdsSub?.unsubscribe();
  }

  private patchColumnResizeForZoom(table?: Table): void {
    if (!table || this.patchedTables.has(table)) {
      return;
    }

    const originalBegin = table.onColumnResizeBegin?.bind(table);
    const originalMove = table.onColumnResize?.bind(table);

    if (!originalBegin || !originalMove) {
      return;
    }

    table.onColumnResizeBegin = (event: Event) => {
      originalBegin(event);
      this.adjustResizeBaselineForZoom(table);
    };

    table.onColumnResize = (event: Event) => {
      originalMove(event);
      this.adjustResizeHelperLeftForZoom(table);
    };

    this.patchedTables.add(table);
  }

  private adjustResizeHelperLeftForZoom(table: Table): void {
    const zoom = this.getActiveSystemZoom();
    if (Math.abs(zoom - 1) < 0.001) {
      return;
    }

    const helperEl = table.resizeHelperViewChild?.nativeElement as HTMLElement | undefined;
    if (!helperEl) {
      return;
    }

    const currentLeft = Number.parseFloat(helperEl.style.left || '');
    if (Number.isFinite(currentLeft)) {
      helperEl.style.left = `${currentLeft / zoom}px`;
    }
  }

  private adjustResizeBaselineForZoom(table: Table): void {
    const zoom = this.getActiveSystemZoom();
    if (Math.abs(zoom - 1) < 0.001) {
      return;
    }

    if (typeof table.lastResizerHelperX === 'number') {
      table.lastResizerHelperX = table.lastResizerHelperX / zoom;
    }
  }

  private getActiveSystemZoom(): number {
    if (typeof window === 'undefined') {
      return 1;
    }

    const rawZoom = getComputedStyle(document.documentElement).getPropertyValue('--systemZoom').trim();
    const zoom = Number.parseFloat(rawZoom);
    return Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
  }

  // ##############################################################################
  // PRESENTATION LAYER / GUI ROUTINES (CALL-BACK ETC.)
  // ##############################################################################

      sortData(sortBy: string): void {
        // Prevent sorting if a column resize is in progress
        if (this.isResizing) {
          console.log('Skipping sort - resize in progress');
          return;
        }
        
        if (this.sortBy === sortBy) {
          this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
        } else {
          this.sortBy = sortBy;
          // Modified and Size columns default to descending, all others to ascending
          this.sortOrder = DEFAULT_DESC_SORT_COLUMNS.has(sortBy) ? 'desc' : 'asc';
        }
        this.persistSort();
        this.recomputeSortedNotes();
        // this.paginateData();
      }
      
      onColumnResizeEnd(): void {
        // Set flag when resize happens
        this.isResizing = true;
        // Delay reset to ensure click event doesn't fire after resize
        setTimeout(() => {
          this.isResizing = false;
        }, 100);
      }
    



  toggleDarkMode() {
    this.layoutService.appState.update((state) => ({
      ...state,
      darkMode: !state.darkMode,
    }));
  }

  mapNotesToCategories(notes: Note[]): NoteCategory[] {
    const allNotesByCategory: NoteCategory[] = [];

    notes.forEach((note) => {
      // console.log('Processing note:', note._id, note.Categories);
      note.Categories?.forEach((categoryID) => {
        // console.log(' → adding mapping:', note._id, '→', categoryID);
        allNotesByCategory.push({
          NoteID: note._id,
          CategoryID: categoryID,
          isActive: true,
        });
      });
    });

    //   console.log('Final mappings:', allNotesByCategory);
    return allNotesByCategory;
  }

  DEBUG_printAssignedSortOrders(projects: Project[] | undefined, card_sort: CardSort[]) {
    //console.log("Assigned Sort Orders:");
    card_sort.forEach((card) => {
      //   const project = projects.find(p => p.ID === card.project_id);
      const project = (projects ?? []).find((p) => p.ID === card.project_id);
      if (project) {
        console.log(`Project: ${project.Name}, Sort Order: ${card.sort_order}`);
      } else {
        console.log(`Project not found for project_id: ${card.project_id}`);
      }
    });
  }

  assignSortOrderToProjects(
    projects: Project[] | undefined,
    noteItems: Note[],
    card_sort: CardSort[],
  ) {
    // First, filter out the projects that already have a sort order assigned in card_sort
    const projectHasSort = new Set(card_sort.map((cs) => cs.project_id));
    // console.log(`projectHasSort: ${[...projectHasSort]}`);

    // Filter note-items that belong to projects that haven't been sorted yet
    const unsortedNoteItems = noteItems.filter((note) => !projectHasSort.has(note.Project));

    // Sort the note-items by last_modified in descending order (latest first)
    const sortedNoteItems = unsortedNoteItems.sort((a, b) => {
      return new Date(b.last_modified).getTime() - new Date(a.last_modified).getTime();
    });

    // Start assigning sort orders, beginning with the value after the last assigned sort order
    let nextSortValue = card_sort.length + 1;

    // Track projects that have been assigned a sort order in this function call
    const assignedProjects = new Set<number>();

    // Assign sort order based on the sorted note-items
    sortedNoteItems.forEach((note) => {
      const project = (projects ?? []).find((p) => p.ID === note.Project);

      if (project && !projectHasSort.has(project.ID) && !assignedProjects.has(project.ID)) {
        // Add the project to the card_sort with the next sort value
        card_sort.push({ project_id: project.ID, sort_order: nextSortValue });
        nextSortValue++;
        // Mark the project as assigned
        assignedProjects.add(project.ID);
      }
    });

    // Optionally: sort the card_sort array by sort_order if needed
    card_sort.sort((a, b) => a.sort_order - b.sort_order);
  }

  sortNotesByCardSort(): void {
    // Create a map from project_id to sort_order for quick lookup
    const sortOrderMap = new Map<number, number>();
    this.card_sort.forEach((card) => {
      sortOrderMap.set(card.project_id, card.sort_order);
    });

    // Sort the data array based on the sort_order in card_sort
    this.data.sort((a, b) => {
      const sortOrderA = sortOrderMap.get(a.Project) ?? Number.MAX_SAFE_INTEGER;
      const sortOrderB = sortOrderMap.get(b.Project) ?? Number.MAX_SAFE_INTEGER;
      return sortOrderA - sortOrderB;
    });

    // Update paginatedData after sorting
    //this.paginateData();
  }

  //   sortProjectsByCardSort(): void {
  //     // Create a map from project_id to sort_order for quick lookup
  //     const sortOrderMap = new Map<number, number>();
  //     this.card_sort.forEach((card) => {
  //       sortOrderMap.set(card.project_id, card.sort_order);
  //     });

  //     // Sort the projects array based on the sort_order in card_sort
  //     this.projects?.sort((a, b) => {
  //       const sortOrderA = sortOrderMap.get(a.ID) ?? Number.MAX_SAFE_INTEGER;
  //       const sortOrderB = sortOrderMap.get(b.ID) ?? Number.MAX_SAFE_INTEGER;
  //       return sortOrderA - sortOrderB;
  //     });
  //   }

  getNoteYearPrefix(note: Note): string {
    const now = new Date();
    const modified = new Date(note.last_modified);

    const yearsDiff = now.getFullYear() - modified.getFullYear();
    const monthsDiff = now.getMonth() - modified.getMonth();
    let totalMonths = yearsDiff * 12 + monthsDiff;

    // If current day is less than the modified day, subtract one (not a full month yet)
    if (now.getDate() < modified.getDate()) {
      totalMonths = totalMonths - 1;
    }

    if (totalMonths) {
      return `${totalMonths}m: `;
    } else {
      return '';
    }
  }

  getNotesForCategory(categoryId: string): Note[] {
    const noteIdSet = new Set(
      this.allNotesByCategory.filter((nc) => nc.CategoryID === categoryId).map((nc) => nc.NoteID),
    );

    return this.notes
      .filter((note) => noteIdSet.has(note._id))
      .sort((a, b) => Date.parse(b.last_modified) - Date.parse(a.last_modified));
  }


  // count the number of filtered notes for a category
  calculateItemCount(categoryID: string): number {
    //console.log("Count ",categoryID," = ",this.allNotesByCategory.filter(rec => rec.CategoryID === categoryID).length);
    return this.allNotesByCategory.filter(rec => rec.CategoryID === categoryID).length;
    // return 1;
  }

  viewAllNotes(projectId: number): void {
    // this.router.navigate(['/project', projectId]);
  }

  toggleHeaderVisibility(): void {
    //console.log("toggle header now =",!this.isHeaderVisible);
    this.isHeaderVisible = !this.isHeaderVisible;
  }

  clickCardHeader(category: Category): void {
    console.log('clickCardHeader() - Category: ', category.Path);
    //this.filter.CategoryPath = category.Path;
    // this.router
    //   .navigate(['/list'], {
    //     state: { filter: this.filter },
    //   })
    //   .then(() => {
    //     console.log('cards:clickCardHeader() - Navigation completed. State sent.');
    //   });
  }

  clickRow(row: Note) {
    if (this.isSelectMode) {
      this.toggleSelection(row);
      return;
    }
    
    console.log('clickRow() - Note: ', row.Title);
    this.router.navigate(['view', row._id], {
      state: { filter: this.filter },
    });
  }

  toggleSelection(note: Note, event?: MouseEvent | boolean) {
    if (event && typeof event === 'object' && 'stopPropagation' in event) {
      event.stopPropagation();
    }
    console.log('toggleSelection() - Note: ', note.Title);
    const id = note._id;
    if (!id) {
      return;
    }

    const isAlreadySelected = this.isNoteSelected(id);
    const updatedSelection = isAlreadySelected
      ? this.selectedNoteIds.filter((n) => n !== id)
      : [...this.selectedNoteIds, id];

    this.selectedNoteIdsChange.emit(updatedSelection);
  }

  isNoteSelected(id: string | undefined): boolean {
    if (!id) {
      return false;
    }
    return this.selectedNoteIds.includes(id);
  }

  onHeaderDoubleClick(sortBy: string): void {
    this.sortBy = sortBy;
    this.sortOrder = 'asc';
    this.persistSort();
    this.recomputeSortedNotes();
  }

  /** Cached result of the sort below — bound directly in the template instead of
   * a getter. A getter re-sorts the entire (potentially large) note list on every
   * Angular change-detection pass that touches this component, which under default
   * change detection could mean every click anywhere in the app, not just here. */
  sortedNotes: Note[] = [];

  private recomputeSortedNotes(): void {
    const notes = [...(this.notes || [])];
    if (!this.sortBy || this.sortBy === 'Link') {
      this.sortedNotes = notes;
      return;
    }

    const dir = this.sortOrder === 'desc' ? -1 : 1;

    if (this.sortBy === 'Size') {
      this.sortedNotes = notes.sort((a, b) => dir * ((a.Size ?? 0) - (b.Size ?? 0)));
      return;
    }

    const getVal = (note: Note, key: string): string => {
      switch (key) {
        case 'Tags':
          return this.getTagNames(note).toLowerCase();
        case 'Title':
          return (note.Title || '').toLowerCase();
        case 'Modified':
          return note.last_modified || '';
        case 'Status':
          return (note.Status || '').toLowerCase();
        case 'Type':
          return (note.NoteType || '').toLowerCase();
        case 'ID':
          return (note._id || '').toLowerCase();
        default:
          return '';
      }
    };

    this.sortedNotes = notes.sort((a, b) => {
      const aVal = getVal(a, this.sortBy);
      const bVal = getVal(b, this.sortBy);
      return dir * aVal.localeCompare(bVal);
    });
  }

  getNoteSize(note: Note): string {
    return ((note.Size ?? 0) / 1024).toFixed(1);
  }

  getTagNames(note: Note): string {
    if (!note.Tags?.length || !this.tags?.length) {
      return '';
    }
    return this.getTagDisplayItems(note)
      .map((tag) => tag.name)
      .join(', ');
  }

  /** Cache of note._id → resolved tag chips, rebuilt only when notes/tags actually
   * change (see ngOnChanges/rebuildTagDisplayCache). Previously this rebuilt a
   * Map of every tag in the system, per note row, on every change-detection pass —
   * O(rows × tags) work redone dozens of times a minute as the note/tag count grew. */
  private tagDisplayCache = new Map<string, TagDisplayItem[]>();

  private rebuildTagDisplayCache(): void {
    this.tagDisplayCache = new Map<string, TagDisplayItem[]>();
    if (!this.tags?.length || !this.notes?.length) {
      return;
    }

    const byId = new Map(this.tags.map((tag) => [tag._id, tag]));

    for (const note of this.notes) {
      if (!note.Tags?.length || !note._id) {
        continue;
      }

      const items = note.Tags
        .map((tag) => {
          const resolvedTag = typeof tag === 'string' ? byId.get(tag) : byId.get(tag._id ?? '') ?? tag;

          if (!resolvedTag?.name) {
            return null;
          }

          return {
            name: resolvedTag.name,
            color: resolvedTag.color,
          } as TagDisplayItem;
        })
        .filter((tag): tag is TagDisplayItem => !!tag);

      this.tagDisplayCache.set(note._id, items);
    }
  }

  getTagDisplayItems(note: Note): TagDisplayItem[] {
    if (!note._id) {
      return [];
    }
    return this.tagDisplayCache.get(note._id) ?? [];
  }

  getCategoryPaths(note: Note): string {
    if (!note.Categories?.length || !this.categories?.length) {
      return '';
    }
    const byId = new Map(this.categories.map((c) => [c._id, c.Path || '']));
    return note.Categories.map((id) => byId.get(id)).filter((p): p is string => !!p).join(', ');
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  isVisible(element: HTMLElement): boolean {
    const style = window.getComputedStyle(element);
    return (
      element.offsetParent !== null && style.visibility !== 'hidden' && style.display !== 'none'
    );
  }

  // ##############################################################################
  // NAVIGATION FUNCTIONS
  // ##############################################################################

  goToListView(): void {
    console.log('goToListView');
    this.router.navigate(['/list'], { state: { filter: this.filter } });
  }

  navigateToEdit(row: Note) {
    console.log(row);
    this.router.navigate(['edit', row._id], {
      state: { filter: this.filter },
    });
  }

  // ##############################################################################
  // MISC LOGIC FUNCTIONS
  // ##############################################################################
}
